/**
 * Custom-field key grouping for the lead card ("More Information").
 *
 * MIRROR of fex-platform-service/src/utils/custom-field-aliases.ts — the
 * backend uses the same table to merge the filter / sort pickers and to match
 * leads, so the card must group keys exactly the way the filters do. Change
 * both files together.
 *
 * Two independent things happen to a raw key:
 *   1. Case / whitespace fold, then SYNONYM fold ("CARREIR", "COMPANY" → carrier).
 *   2. The importer's collision suffix (" 2", " 3" …) is a SECOND POLICY on the
 *      same person — every "Carrier 2" lead also carries a different "Carrier".
 *      It is split off first and put back on the label, never folded away.
 *
 * Brandon (Duse Agency, Sep 2026): 610 distinct keys across 219k leads, the
 * carrier/premium family alone spelled 107 ways. Agents only ever open the card
 * for three of them — Carrier, Annual Premium, Monthly Premium — so those are
 * the KEY fields the card shows by default.
 */

// variant (trim+lowercase) → canonical (trim+lowercase)
const ALIASES: Record<string, string> = {
  'annualized premium': 'annual premium',
  carreir: 'carrier',
  company: 'carrier',
  monthly: 'monthly premium',
  'est .monthly premium': 'monthly premium',
  'est. monthly premium': 'monthly premium',
  'estimated monthly premium': 'monthly premium',
  premium: 'monthly premium',
  // Platform-wide fuzzy scan (2026-09-03): typos / truncations other agencies
  // have that Duse doesn't. All hold plain dollar premiums.
  montly: 'monthly premium',                 // 2,007 leads, 1 agency
  'monthly pre': 'monthly premium',          // 922 — truncated header
  annualized_premium: 'annual premium',      // 1,105 — underscore variant
  'annualized preium': 'annual premium',     // 14 — typo
};

// canonical (trim+lowercase) → preferred display label
const CANONICAL_LABELS: Record<string, string> = {
  'annual premium': 'Annual Premium',
  carrier: 'Carrier',
  'monthly premium': 'Monthly Premium',
};

/** The three fields agents actually look for; shown by default on the card. */
export const KEY_FIELD_CANONICALS = ['carrier', 'annual premium', 'monthly premium'] as const;

/** Canonical normalized key: trim + lowercase + alias fold. (No suffix handling.) */
export function canonicalCustomFieldKey(key: string): string {
  const norm = (key ?? '').trim().toLowerCase();
  return ALIASES[norm] ?? norm;
}

/** Preferred display label for a canonical key, if one is defined. */
export function canonicalCustomFieldLabel(canonicalKey: string): string | undefined {
  return CANONICAL_LABELS[(canonicalKey ?? '').trim().toLowerCase()];
}

/**
 * Split the importer's collision suffix off a raw key. "Carrier 2" → base
 * "Carrier", slot 2. Slot 1 is always the unsuffixed key, so a trailing " 1"
 * is not a slot. Keys like "Est Term 10yr Monthly" are untouched (no trailing
 * bare number).
 */
export function splitPolicySlot(rawKey: string): { base: string; slot: number | null } {
  const m = /^(.*\S)\s+(\d{1,2})$/.exec((rawKey ?? '').trim());
  if (!m || Number(m[2]) < 2) return { base: (rawKey ?? '').trim(), slot: null };
  return { base: m[1], slot: Number(m[2]) };
}

export interface GroupedCustomKey {
  /** canonical of the base key — the grouping bucket ("carrier") */
  canonical: string;
  /** what to print: canonical label (or nicest raw base) + slot ("Carrier 2") */
  label: string;
  slot: number | null;
  isKeyField: boolean;
  /** the raw key as stored on the lead — writes must go here, never the label */
  rawKey: string;
  /** set when a SYNONYM fired (e.g. "COMPANY" shown as Carrier) — the card says so */
  aliasedFrom: string | null;
}

/** Group + relabel one raw key. `fallbackLabel` is what the card would have shown before. */
export function describeCustomKey(rawKey: string, fallbackLabel: string): GroupedCustomKey {
  const { base, slot } = splitPolicySlot(rawKey);
  const canonical = canonicalCustomFieldKey(base);
  const pretty = canonicalCustomFieldLabel(canonical);
  // Only relabel when we KNOW the canonical name; otherwise keep the existing
  // humanised label so unrelated fields look exactly as they always have.
  const baseLabel = pretty ?? (slot != null ? fallbackLabel.replace(/\s+\d{1,2}$/, '') : fallbackLabel);
  const baseNorm = base.trim().toLowerCase();
  return {
    canonical,
    label: slot != null ? `${baseLabel} ${slot}` : baseLabel,
    slot,
    isKeyField: (KEY_FIELD_CANONICALS as readonly string[]).includes(canonical),
    rawKey,
    // Case-only differences ("MONTHLY PREMIUM") are not worth a hint; a real
    // synonym ("COMPANY", "CARREIR", "PREMIUM") is.
    aliasedFrom: baseNorm !== canonical ? base : null,
  };
}

/** One entry of the agency-wide field list (from GET /leads/custom-field-keys). */
export interface CustomFieldOption {
  key: string;
  type: string;
  isKeyField: boolean;
}

/**
 * Prepare the agency-wide key list for a picker (filter, sort, assignment):
 *   - drop internal markers (`_sourceAgencyId`, `_originalLeadStat`, …)
 *   - merge synonyms/typos into one option under the canonical label
 *     ("CARREIR" + "COMPANY" + "Carrier" → one "Carrier")
 *   - key fields (Carrier, Annual Premium, Monthly Premium) first, then A→Z
 * The option's `key` is what gets sent to the API; the backend expands it to
 * the whole alias group, so picking "Carrier" also matches the CARREIR leads.
 */
export function groupCustomFieldOptions(fields: Array<{ key: string; type: string }>): CustomFieldOption[] {
  const byCanonical = new Map<string, CustomFieldOption>();
  for (const f of fields) {
    const raw = typeof f?.key === 'string' ? f.key.trim() : '';
    if (!raw || raw.startsWith('_')) continue;
    const { base, slot } = splitPolicySlot(raw);
    const canonical = canonicalCustomFieldKey(base);
    const bucket = slot != null ? `${canonical} ${slot}` : canonical;
    const pretty = canonicalCustomFieldLabel(canonical);
    const label = pretty ? (slot != null ? `${pretty} ${slot}` : pretty) : raw;
    const isKeyField = (KEY_FIELD_CANONICALS as readonly string[]).includes(canonical);
    const existing = byCanonical.get(bucket);
    if (!existing) {
      byCanonical.set(bucket, { key: label, type: f.type, isKeyField });
    } else if (existing.type !== 'numeric' && f.type === 'numeric') {
      existing.type = 'numeric'; // any numeric variant → sort as numbers
    }
  }
  const order = (c: CustomFieldOption) => {
    const { base, slot } = splitPolicySlot(c.key);
    const i = (KEY_FIELD_CANONICALS as readonly string[]).indexOf(canonicalCustomFieldKey(base));
    return c.isKeyField ? (slot ?? 1) * 10 + i : Number.MAX_SAFE_INTEGER;
  };
  return [...byCanonical.values()].sort((a, b) => order(a) - order(b) || a.key.localeCompare(b.key));
}

/** localStorage key shared by the lead card and every picker: '1' = key fields only (default), '0' = show all. */
export const KEY_FIELDS_ONLY_STORAGE_KEY = 'lead-custom-fields-key-only';
